export const PICKUP_SEARCH_COLUMNS = [
    { value: 'firstName', label: 'First Name', icon: 'bi-person', theme: 'indigo' },
    { value: 'lastName', label: 'Last Name', icon: 'bi-person-badge', theme: 'purple' },
    { value: 'preferredName', label: 'Preferred Name', icon: 'bi-person-heart', theme: 'danger' },
    { value: 'prmId', label: 'PMS ID', icon: 'bi-hash', theme: 'slate' },
    { value: 'prmPickupId', label: 'Pickup ID', icon: 'bi-upc', theme: 'cyan' },
    { value: 'type', label: 'Type', icon: 'bi-eyeglasses', theme: 'violet' },
    { value: 'status', label: 'Status', icon: 'bi-flag', theme: 'warning' },
];

export const PICKUP_STATUS_OPTIONS = [
    { value: 'ALL', label: 'All statuses' },
    { value: 'WAITING', label: 'Waiting' },
    { value: 'SENT', label: 'Sent' },
    { value: 'CONFIRMED', label: 'Confirmed' },
    { value: 'FAILED', label: 'Failed' },
];

export const PICKUP_TYPE_OPTIONS = [
    { value: 'ALL', label: 'All types' },
    { value: 'GLASS', label: 'Glasses' },
    { value: 'CONTACT LENS', label: 'Contact lenses' },
];

export const PICKUP_CONFIRMED_OPTIONS = [
    { value: 'ALL', label: 'All' },
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
];

export const PICKUP_SORT_FIELDS = [
    { value: 'createdAt', label: 'Created date' },
    { value: 'modifiedAt', label: 'Modified date' },
    { value: 'status', label: 'Status' },
    { value: 'type', label: 'Type' },
    { value: 'prmUpdateAt', label: 'PMS update' },
];

export const PAGE_SIZE_OPTIONS = [10, 20, 50];

export const DEFAULT_PICKUP_SEARCH_COLUMN = 'firstName';

export const DEFAULT_PICKUP_FILTERS = {
    status: 'ALL',
    type: 'ALL',
    isConfirmed: 'ALL',
};

export const DEFAULT_PICKUP_SORT = {
    sortBy: 'createdAt',
    sortOrder: 'DESC',
};

export const DEFAULT_PICKUP_PAGE_SIZE = 20;

export const formatPickupCount = (value) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
        return '0';
    }

    return number.toLocaleString();
};

const EXACT_SEARCH_FIELDS = new Set(['status', 'type', 'patientId', 'prmId', 'prmPickupId']);

const appendSearchFilter = (parts, field, value) => {
    const trimmed = value?.trim();
    if (!trimmed) {
        return;
    }

    if (EXACT_SEARCH_FIELDS.has(field)) {
        parts.push(`${field}==${trimmed}`);
        return;
    }

    parts.push(`${field}==*${trimmed}*`);
};

export const buildPickupsQuery = (
    {
        searchColumn = DEFAULT_PICKUP_SEARCH_COLUMN,
        searchTerm = '',
        showAll = false,
        status = DEFAULT_PICKUP_FILTERS.status,
        type = DEFAULT_PICKUP_FILTERS.type,
        isConfirmed = DEFAULT_PICKUP_FILTERS.isConfirmed,
    } = {},
    sort = DEFAULT_PICKUP_SORT
) => {
    const parts = [];

    if (!showAll) {
        appendSearchFilter(parts, searchColumn, searchTerm);
    }

    if (status && status !== 'ALL') {
        parts.push(`status==${status}`);
    }

    if (type && type !== 'ALL') {
        parts.push(`type==${type}`);
    }

    if (isConfirmed === 'true') {
        parts.push('isConfirmed==true');
    } else if (isConfirmed === 'false') {
        parts.push('isConfirmed==false');
    }

    if (sort?.sortBy) {
        parts.push(`sortBy==${sort.sortBy}`);
    }

    if (sort?.sortOrder) {
        parts.push(`sortOrder==${sort.sortOrder.toUpperCase()}`);
    }

    return parts.join(',');
};

export const formatPickupPatientName = (pickup = {}) => {
    const fullName = [pickup.patientFirstName, pickup.patientLastName].filter(Boolean).join(' ');

    if (!fullName) {
        return 'Unknown patient';
    }

    if (pickup.patientPreferredName) {
        return `${fullName} (${pickup.patientPreferredName})`;
    }

    return fullName;
};

export const getPickupStatusMeta = (status) => {
    switch (String(status || '').toUpperCase()) {
        case 'WAITING':
            return {
                label: 'Waiting',
                className: 'optical-pickup-status--waiting',
                icon: 'bi-hourglass-split',
            };
        case 'SENT':
            return {
                label: 'Sent',
                className: 'optical-pickup-status--sent',
                icon: 'bi-send',
            };
        case 'CONFIRMED':
            return {
                label: 'Confirmed',
                className: 'optical-pickup-status--confirmed',
                icon: 'bi-check-circle',
            };
        case 'FAILED':
            return {
                label: 'Failed',
                className: 'optical-pickup-status--failed',
                icon: 'bi-x-circle',
            };
        default:
            return {
                label: status || 'Unknown',
                className: 'optical-pickup-status--unknown',
                icon: 'bi-question-circle',
            };
    }
};

export const getPickupTypeMeta = (type) => {
    const normalized = String(type || '').toUpperCase();

    if (normalized.includes('GLASS')) {
        return { label: 'Glasses', icon: 'bi-eyeglasses' };
    }

    if (normalized.includes('LENS') || normalized.includes('CONTACT')) {
        return { label: 'Contact lenses', icon: 'bi-eye' };
    }

    return { label: type || 'Pickup', icon: 'bi-box-seam' };
};

export const formatPickupDateTime = (value) => {
    if (!value) {
        return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};
